/*****
 * Sort Parameters
 * Interface, classes and type definition for converting between field names passed in a query parameter to the
 * database mapping for the field name.
 */

/**
 * Sort Settings Interface
 * Defines an interface for specifying a database name and default direction.
 * Can also include a single level of secondary sorts
 */
export interface SortSettingInterface {
  dbName: string;
  defaultOrder?: SortOrder;
  secondarySort?: {
    dbName: string;
    order?: SortOrder;
  };
}

/**
 * Sort Mapping
 * Typing for valid sort fields and translation to database names
 * The key should be all lowercase
 * The value should either be the database field, or a SortSettingsInterface if you need more control or secondary sorts
 */
export type SortMapping = { [key: string]: SortSettingInterface | string };

/**
 * Generated sort clauses that can be used for generating database and search queries
 */
export interface SortClause {
  paramName: string;
  order: 'ASC' | 'DESC';
  dbName: string;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/**
 * Sort Params
 * Used to construct/deconstruct sort parameters to and from a standardized string.
 * These strings can then be passed as parameters on query strings.
 */
export class SortParams {
  /**
   * Parse
   * Parses a string of sort params (normally pulled from a requests parameters)
   * The string must be in the form of a comma separated list of the fields to sort by.
   * Each field name can be immediately followed by [asc]/[desc] or +/-.  If nothing is specified, defaults to 'asc'
   * Parameters will be converted to all lowercase for consistency.
   * @param sortString  String pulled from the query parameters representing the fields to sort by
   * @param dbMappings Mapping of query parameter names to database names.  Also used to filter out only valid sort fields.
   */
  public static parse(
    sortString: string | string[],
    dbMappings: SortMapping,
  ): SortParams {
    const clauses: SortClause[] = [];
    if (sortString) {
      let sorts: string[];

      if (Array.isArray(sortString)) {
        sorts = sortString;
      } else {
        sorts = sortString.toLowerCase().split(',');
      }

      for (let part of sorts) {
        // Extract param name and any ordering info
        let paramName = part;
        let order: SortOrder | undefined;
        part = part.trim();
        const lc = part[part.length - 1];
        if (lc === ']') {
          const lc2 = part.split('[');
          if (lc2.length === 2) {
            paramName = lc2[0];
            if (lc2[1][0] === 'd') {
              order = SortOrder.DESC;
            } else if (lc2[1][0] === 'a') {
              order = SortOrder.ASC;
            }
          }
        } else if (lc === '+' || lc === '-') {
          paramName = part.substring(0, part.length - 1);
          if (lc === '-') {
            order = SortOrder.DESC;
          }
        }

        paramName = paramName.trim();
        // Check if the caller passed in a set of valid sort fields, and only add if the paramName is in it.
        const dbSetting = dbMappings[paramName];
        if (dbSetting) {
          if (typeof dbSetting === 'object') {
            clauses.push({
              paramName,
              order: order || dbSetting.defaultOrder || SortOrder.ASC,
              dbName: dbSetting.dbName,
            });

            // Check if we have defined a secondary sort.
            if (dbSetting.secondarySort) {
              clauses.push({
                paramName: dbSetting.secondarySort.dbName,
                order: dbSetting.secondarySort.order || SortOrder.ASC,
                dbName: dbSetting.secondarySort.dbName,
              });
            }
          } else {
            clauses.push({
              paramName,
              order: order || SortOrder.ASC,
              dbName: dbSetting,
            });
          }
        }
      }
    }

    return new SortParams(clauses);
  }

  public sortClauses: SortClause[];

  constructor(clauses?: SortClause[]) {
    if (clauses) {
      this.sortClauses = clauses;
    } else {
      this.sortClauses = [];
    }
  }
}
